import { trpc } from '../lib/trpc.js';
import { ZoneBadge } from '../components/ui/ZoneBadge.js';
import { formatGbp, formatPct, formatDate } from '../lib/format.js';
import { scoreCreative, getCplZone } from '@lcc/shared';
import { clsx } from 'clsx';
import { Link } from '@tanstack/react-router';

function RecommendationBadge({ rec }: { rec: string }) {
  return (
    <span className={clsx(
      'text-xs font-semibold px-2 py-0.5 rounded',
      rec === 'SCALE' && 'bg-green-100 text-green-800',
      rec === 'HOLD' && 'bg-blue-50 text-blue-800',
      rec === 'KILL' && 'bg-red-100 text-red-800',
      rec === 'INSUFFICIENT_DATA' && 'bg-gray-100 text-gray-600',
    )}>
      {rec.replace(/_/g, ' ')}
    </span>
  );
}

export function CreativesPage() {
  const { data, isLoading } = trpc.creatives.list.useQuery();

  if (isLoading) return <div className="animate-pulse text-gray-400 p-8">Loading creatives…</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl text-navy">Creative Scorecard</h1>
        <p className="text-xs text-gray-500">14-day rolling window</p>
      </div>

      {!data || data.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-500">No creatives yet.</p>
          <p className="text-sm text-gray-400 mt-1">Upload a Meta CSV or add creatives manually.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {data.map((c: { id: string; adName: string; displayName: string; brand: string; status: string; launchedAt?: Date | string | null; metrics14d: { adName: string; spendGbp: number; leads: number; cpl: number; leadToBookRate: number; revenue: number; daysActive: number; bookings: number; ctr: number }; score: { overallScore: number; recommendation: string; reasoning: string[] }; zone: string | null }) => {
            const metrics = c.metrics14d;
            const score = scoreCreative(metrics);
            const zone = metrics.cpl > 0 ? getCplZone(metrics.cpl) : null;

            return (
              <div key={c.id} className="card">
                <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="font-display text-xl text-navy">{c.displayName}</h2>
                      <span className={clsx(
                        'text-xs px-2 py-0.5 rounded border',
                        c.brand === 'LCC' ? 'bg-navy/5 border-navy/20 text-navy' : 'bg-gray-100 border-gray-200 text-gray-600'
                      )}>
                        {c.brand}
                      </span>
                      <span className="text-xs text-gray-400 border border-gray-200 px-2 py-0.5 rounded">{c.status}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1 font-mono">{c.adName}</p>
                    {c.launchedAt && <p className="text-xs text-gray-400">Launched {formatDate(c.launchedAt)}</p>}
                  </div>
                  <div className="flex items-center gap-3">
                    {zone && <ZoneBadge zone={zone} size="md" />}
                    <RecommendationBadge rec={score.recommendation} />
                  </div>
                </div>

                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Spend</p>
                    <p className="font-mono font-medium text-navy">{formatGbp(metrics.spendGbp)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Leads</p>
                    <p className="font-mono font-medium text-navy">{metrics.leads}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">CPL</p>
                    <p className="font-mono font-medium text-navy">{metrics.cpl > 0 ? formatGbp(metrics.cpl) : '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">CTR</p>
                    <p className="font-mono font-medium text-navy">{metrics.ctr > 0 ? formatPct(metrics.ctr) : '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Lead→Book</p>
                    <p className="font-mono font-medium text-navy">{formatPct(metrics.leadToBookRate)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider">Revenue</p>
                    <p className="font-mono font-medium text-navy">{formatGbp(metrics.revenue)}</p>
                  </div>
                </div>

                {score.reasoning.length > 0 && (
                  <div className="border-t border-gray-50 pt-3">
                    <p className="text-xs text-gray-400 mb-1">Scoring ({score.overallScore}/100)</p>
                    <ul className="space-y-0.5">
                      {score.reasoning.map((r, i) => (
                        <li key={i} className="text-xs text-gray-600">· {r}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-3 flex justify-end">
                  <Link to="/creatives/$id" params={{ id: c.id }} className="text-xs text-navy hover:text-gold transition-colors">
                    View details →
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
